import { AnyExtension, Editor } from '@tiptap/core'
import Collaboration from '@tiptap/extension-collaboration'
import * as Y from 'yjs'
import { EDITOR_STRUCTURAL_EXTENSIONS } from '../../../../../backend/src/tiptap/editor-extensions'
import { ProtectAgendaItems, ProtectAgendaOutline } from './extensions/protect-minute-structure'
import { ProtectMinuteTitles } from './extensions/protect-minute-titles'
import { CaretUser, UniqueUserCollaborationCaret } from './extensions/unique-user-collaboration-caret'
import { SseSyncProvider } from './sse-sync-provider'

/** The Y.js document name of a restricted agenda item's scope document. Mirrors the backend `buildScopeDocumentName`. */
function buildScopeDocumentName(meetingId: number, templateItemId: string): string {
  return `minutes:${meetingId}:scope:${templateItemId}`
}

export type ScopeEditorOptions = {
  meetingId: number
  templateItemId: string
  element: HTMLElement
  caretUser: CaretUser
  editable: boolean
  forcePolling: boolean
  /** Make each agenda item fixed — its title read-only, no items added or removed — leaving only its content editable. */
  protectAgendaOutline?: boolean
  /** Called once, when the scope document has first synced. */
  onSynced?: () => void
  onStatelessPayload?: (payload: string) => void
  /** Further extensions for the scope editor, such as the host's citations. */
  extensions?: AnyExtension[]
}

export type ScopeEditorHandle = {
  editor: Editor
  destroy: () => void
}

/**
 * A self-contained collaborative editor bound to one restricted agenda item's scope document. Each has its own
 * Y.Doc and provider connection, so a user only ever loads the scopes they are authorized for. Scope documents
 * always poll (and never report a fallback), matching the base document's transport whenever it holds restricted items.
 */
export function createScopeEditor(options: ScopeEditorOptions): ScopeEditorHandle {
  const document = new Y.Doc()
  let hasSynced = false
  const provider = new SseSyncProvider({
    name: buildScopeDocumentName(options.meetingId, options.templateItemId),
    document,
    forcePolling: options.forcePolling,
    suppressFallbackReport: true,
    onStatelessPayload: options.onStatelessPayload,
    onSynced: ({ state }) => {
      if (state && !hasSynced) {
        hasSynced = true
        options.onSynced?.()
      }
    },
  })

  const editor = new Editor({
    element: options.element,
    extensions: [
      ...EDITOR_STRUCTURAL_EXTENSIONS,
      ProtectAgendaItems,
      ...(options.protectAgendaOutline === true ? [ProtectMinuteTitles, ProtectAgendaOutline] : []),
      Collaboration.configure({ document }),
      UniqueUserCollaborationCaret.configure({ provider, user: options.caretUser }),
      ...(options.extensions ?? []),
    ],
  })
  editor.setEditable(options.editable)

  return {
    editor,
    destroy: () => {
      editor.destroy()
      provider.destroy()
      document.destroy()
    },
  }
}
